import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, MessageCircle, FileDown, FileText, Loader2, User } from 'lucide-react';
import { candidatesAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { buildResumePdf } from '../resume/buildResumePdf';

const photoSrc = (url) => (url && url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);

export const CandidateContactCard = ({ candidateId }) => {
  const [p, setP] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    setP(null); setError(null);
    candidatesAPI.getById(candidateId).then((r) => setP(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'Contato indisponível'));
  }, [candidateId]);

  const downloadAnexo = async () => {
    setBusy('anexo');
    try {
      const r = await candidatesAPI.curriculoFile(candidateId);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.data);
      a.download = p.curriculo_nome || `curriculo-${(p.nome || 'candidato').replace(/\s+/g, '-')}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error('Não foi possível baixar o currículo'); } finally { setBusy(null); }
  };

  const downloadG2 = async () => {
    setBusy('g2');
    try { await buildResumePdf(p, p.email); } catch { toast.error('Erro ao gerar currículo'); } finally { setBusy(null); }
  };

  if (error) return <p className="text-sm text-slate-500" data-testid="contact-card-error">{error}</p>;
  if (!p) return <Loader2 className="w-5 h-5 animate-spin text-cyan-600" />;
  const phone = (p.telefone || '').replace(/\D/g, '');
  const local = [p.cidade, p.estado].filter(Boolean).join(' - ') || p.localizacao;
  return (
    <div className="rounded-xl border border-cyan-200 bg-cyan-50/40 p-4 space-y-3" data-testid="candidate-contact-card">
      <div className="flex items-center gap-3">
        {p.foto_url
          ? <img src={photoSrc(p.foto_url)} alt="" className="w-14 h-14 rounded-full object-cover" />
          : <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center"><User className="w-7 h-7 text-slate-400" /></div>}
        <div className="min-w-0">
          <p className="font-semibold text-slate-900 text-lg leading-tight" data-testid="contact-name">{p.nome}</p>
          {local && <p className="text-sm text-slate-600 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{local}</p>}
        </div>
      </div>
      <div className="space-y-1 text-sm">
        {p.telefone && <a href={`tel:${phone}`} className="flex items-center gap-2 text-slate-800 hover:text-cyan-700" data-testid="contact-phone"><Phone className="w-4 h-4 text-cyan-600" />{p.telefone}</a>}
        {p.email && <a href={`mailto:${p.email}`} className="flex items-center gap-2 text-slate-800 hover:text-cyan-700 break-all" data-testid="contact-email"><Mail className="w-4 h-4 text-cyan-600" />{p.email}</a>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {phone && (
          <Button asChild size="sm" className="bg-green-600 hover:bg-green-700 sm:col-span-2" data-testid="contact-whatsapp">
            <a href={`https://wa.me/55${phone}`} target="_blank" rel="noreferrer"><MessageCircle className="w-4 h-4 mr-2" />Chamar no WhatsApp</a>
          </Button>
        )}
        {p.curriculo_url && (
          <Button size="sm" variant="outline" onClick={downloadAnexo} disabled={!!busy} data-testid="contact-download-cv">
            {busy === 'anexo' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}Currículo anexado
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={downloadG2} disabled={!!busy} className={p.curriculo_url ? '' : 'sm:col-span-2'} data-testid="contact-download-g2-cv">
          {busy === 'g2' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}Currículo G2eGente (PDF)
        </Button>
      </div>
    </div>
  );
};
